import {
    _GetSelectedProduct,
    type ClickAndCollectUnavailableProductsViewModel,
    type OnlineUnavailableProductsViewModel,
    DeleteMultipleFromBasket,
    type ClickAndCollectBasketStoreViewModel,
    type OnlineDeliveryOption,
    type ProductCustomerNotificationModel
} from 'autogen/swagger/all';
import ProductFavoriteButton from 'components/generic/productFavoriteButton/ProductFavoriteButton';
import { ProductImage } from 'components/generic/productImage/ProductImage';
import { ProductImageContainer } from 'components/generic/productImage/shared/styles';
import { Typography } from 'components/generic/typography';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { customScrollbar } from 'modules/helpers/mixins';
import FlexColumn from 'components/Layout/components/Flex/components/FlexColumn';
import ActionFooter from 'components/quickView/components/ActionFooter';
import { Button } from 'components/Button';
import InlineButton from 'components/generic/InlineButton';
import NotificationForm from 'components/product/notification';
import { useQuickView, useSlides } from 'components/quickView';
import { updateBasket } from 'components/basket/shared/basket';
import { useDeliveryFixStore } from 'components/pages/Basket/shared/deliveryFixStore';
import {
    DeleteProductItemsButton,
    SoldOutOnlineButtonText,
    SoldOutOnlineText
} from 'autogen/translation-keys/trans-website-basket-deliveryfix';
import useGA4Tracking from 'modules/tracking/GA4/GA4Tracking';

export type RemovedProductItemsProps = {
    model?: ClickAndCollectUnavailableProductsViewModel | OnlineUnavailableProductsViewModel;
    selectedDeliveryMethod?: ClickAndCollectBasketStoreViewModel | OnlineDeliveryOption;
    selectedTab: number;
};

const StyledFlexColumn = styled(FlexColumn)`
    width: 100%;
    justify-content: space-between;
`;

const ScrollContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.Spacing24};
    padding: 0 ${({ theme }) => theme.Spacing24} ${({ theme }) => theme.Spacing24};
    overflow: hidden scroll;
    -webkit-overflow-scrolling: touch;
    ${customScrollbar}
`;

const ProductContainer = styled.div<{ $twoColumns?: boolean }>`
    display: grid;
    width: 100%;
    gap: ${({ theme }) => theme.Spacing16};
    grid-template-columns: ${({ $twoColumns }) => ($twoColumns ? '1fr 1fr' : '1fr')};
`;

const StyledImageContainer = styled(ProductImageContainer)`
    position: relative;
    padding: ${({ theme }) => theme.Spacing24};
`;

const StyledProductFavoriteButton = styled(ProductFavoriteButton)`
    position: absolute;
    top: ${({ theme }) => theme.Spacing12};
    right: ${({ theme }) => theme.Spacing12};
`;

function RemovedProductItems(props: RemovedProductItemsProps) {
    const { model, selectedDeliveryMethod, selectedTab } = props;

    if (!model) return null;

    const { unavailableProducts, heading } = model;
    const { t } = useTranslation();
    const { push } = useSlides();
    const { toggle } = useQuickView('antiBreakQuickView');
    const setShowDeliveryFixBasketMessage = useDeliveryFixStore((state) => state.setShowDeliveryFixBasketMessage);
    const GA4Tracking = useGA4Tracking();

    function isClickAndCollectDelivery(
        model: RemovedProductItemsProps['selectedDeliveryMethod']
    ): model is ClickAndCollectBasketStoreViewModel {
        return (model as ClickAndCollectBasketStoreViewModel).model !== undefined;
    }

    async function handleOpenNotificationForm() {
        GA4Tracking({
            eventName: 'notification_request',
            category: 'ecommerce',
            context: 'basket',
            eventParams: [{ param_name: 'notification_type', param_value: 'back_in_stock' }]
        });

        const products: ProductCustomerNotificationModel[] = unavailableProducts.map(({ ...product }) => {
            return {
                productId: product.id,
                image: product.image.url,
                productName: product.name,
                isPreorder: false
            };
        });

        push(() => <NotificationForm model={products} />);
    }

    async function handleOnClick() {
        if (isClickAndCollectDelivery(selectedDeliveryMethod)) {
            await DeleteMultipleFromBasket({
                deletedProductIds: unavailableProducts.map(({ ...product }) => product.id),
                storeId: selectedDeliveryMethod?.model.id
            });
        } else {
            await DeleteMultipleFromBasket({
                deletedProductIds: unavailableProducts.map(({ ...product }) => product.id)
            });
        }
        const deliveryType = selectedTab === 1 ? 'Online' : 'ClickAndCollect';

        GA4Tracking({
            eventName: 'submit',
            context: 'basket',
            category: 'ecommerce',
            eventParams: [
                { param_name: 'modal_type', param_value: 'anti_break' },
                { param_name: 'delivery_method', param_value: deliveryType },
                { param_name: 'removed_products', param_value: unavailableProducts.length }
            ]
        });

        setShowDeliveryFixBasketMessage(true);
        updateBasket();
        toggle(false);
    }

    return (
        <StyledFlexColumn>
            <ScrollContainer role="alert">
                <Typography.Headline size="M">{heading}</Typography.Headline>
                <Typography.Body>
                    {t(SoldOutOnlineText)} -{' '}
                    <InlineButton onClick={handleOpenNotificationForm}>{t(SoldOutOnlineButtonText)}</InlineButton>
                </Typography.Body>
                <ProductContainer $twoColumns={unavailableProducts.length > 1}>
                    {unavailableProducts.map((product, i) => (
                        <StyledImageContainer key={i}>
                            <ProductImage image={product.image} width={300} transformType={'WidthScale'} />
                            <StyledProductFavoriteButton
                                initialFavoriteState={product.favoriteStatus === 'IsFavorite'}
                                productId={product.id}
                                variant="favoriteGhostHorizontal"
                                iconSize="lg"
                            />
                        </StyledImageContainer>
                    ))}
                </ProductContainer>
            </ScrollContainer>
            <ActionFooter size="lg">
                <Button size="lg" onClick={handleOnClick}>
                    {t(DeleteProductItemsButton)}
                </Button>
            </ActionFooter>
        </StyledFlexColumn>
    );
}

export default RemovedProductItems;
